import { useCallback, useContext } from 'react'

import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getCollaborationOrganizationId } from '@/analytics/utils/matter-collaboration-analytics'
import { AuthContext } from '@/context/auth-context'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { postDismissMatterBanner } from '@/matter/fetch/post-dismiss-matter-banner'
import { MatterBannerKind } from '@/matter/store/schemas'
import { selectDismissedBannerKinds, selectDismissedBannersLoaded } from '@/matter/store/selectors'
import { MattersActions } from '@/matter/store/slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'

/**
 * Reads and writes the signed-in user's dismissed setup banners for one matter.
 *
 * Dismissals arrive with the matter detail the matter layout already loads, so nothing here fetches
 * on mount. That load races the banners' own status fetches, though, so callers must hold their
 * banner back until `dismissedBannersLoaded` to avoid flashing one the user already cleared.
 */
export function useMatterBannerDismissal(matterId: string | null | undefined) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const dismissedBanners = useAppSelector((state) => selectDismissedBannerKinds(state, matterId ?? undefined))
  const dismissedBannersLoaded = useAppSelector((state) => selectDismissedBannersLoaded(state, matterId ?? undefined))

  const dismissBanner = useCallback(
    (kind: MatterBannerKind) => {
      if (!matterId) return

      // Hide it now, then let the saved set be the source of truth. On failure the optimistic
      // dismissal is reverted, so the banner comes back rather than looking saved when it isn't.
      dispatch(MattersActions.matterBannerDismissed({ matterId, kind }))

      // Two-argument `then` so the reject handler only ever sees a failed save. A `.catch()` here
      // would also swallow a throw from the success handler and revert a dismissal that did save.
      void postDismissMatterBanner(matterId, kind).then(
        (savedKinds) => {
          dispatch(MattersActions.matterDismissedBannersReceived({ matterId, kinds: savedKinds }))
          // Tracked on the confirmed save, not the click, so a reverted dismissal isn't counted.
          trackEvent(AnalyticsEvent.MatterBannerDismissed, {
            matterId,
            organizationId: getCollaborationOrganizationId(authContext?.userAccountData),
            matterBannerKind: kind,
          })
        },
        () => {
          dispatch(MattersActions.matterBannerDismissalReverted({ matterId, kind }))
          dispatch(
            openGlobalToast({
              type: GlobalToastType.ERROR,
              message: 'Couldn’t dismiss this banner. Please try again.',
              durationMs: kToastDurationMediumMs,
            })
          )
        }
      )
    },
    [matterId, dispatch, trackEvent, authContext?.userAccountData]
  )

  return { dismissedBanners, dismissedBannersLoaded, dismissBanner }
}
